import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ShoppingBag, X } from 'lucide-react';
import { previewPurchases, recentPurchases } from '../data/recentPurchases';
const INITIAL_DELAY_MS = 5000;
const VISIBLE_MS = 5000;
const GAP_MS = 4000;
export function PurchaseNotification() {
  const purchases = useMemo(() => recentPurchases.length > 0 ? recentPurchases : isEditorPreview() ? previewPurchases : [], []);
  const [index, setIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (isDismissed || purchases.length === 0) return;
    const delay = isVisible ? VISIBLE_MS : hasStarted ? GAP_MS : INITIAL_DELAY_MS;
    const timer = window.setTimeout(() => {
      if (isVisible) {
        setIsVisible(false);
        setIndex(current => (current + 1) % purchases.length);
      } else {
        setHasStarted(true);
        setIsVisible(true);
      }
    }, delay);
    return () => window.clearTimeout(timer);
  }, [isVisible, isDismissed, hasStarted, purchases.length]);
  if (isDismissed || purchases.length === 0) return null;
  const purchase = purchases[index];
  const minutesLabel = `${purchase.minutesAgo} minute${purchase.minutesAgo === 1 ? '' : 's'} ago`;
  return <div className="fixed bottom-4 left-4 z-40 w-[320px] max-w-[calc(100vw-2rem)] pointer-events-none" aria-live="polite" data-id="element-242" data-mp="19nbsroo2p7eh">
      <AnimatePresence data-id="element-243" data-mp="zrqwm75or1cn">
        {isVisible && <motion.div key={`${purchase.firstName}-${purchase.city}-${index}`} initial={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 16,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 8,
        scale: 0.98
      }} transition={{
        duration: 0.28,
        ease: [0.23, 1, 0.32, 1]
      }} className="pointer-events-auto relative flex items-center gap-3.5 bg-white rounded-2xl border border-sage-200 shadow-xl shadow-forest/20 py-3 pl-3 pr-9 overflow-hidden" role="status" data-id="element-244" data-mp="zt1yg1fklz14">
            <span className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full bg-leaf" aria-hidden="true" data-id="element-245" data-mp="2nmx47u2iv84e" />

            <div className="relative shrink-0 w-11 h-11 rounded-xl bg-sage-100 text-leaf flex items-center justify-center" data-id="element-246" data-mp="2jlldtmbtqiv5">
              <ShoppingBag size={19} strokeWidth={2.2} data-id="element-247" data-mp="3be2kgacdk2f8" />
            </div>

            <div className="min-w-0 flex-1" data-id="element-248" data-mp="37cz3fkilc0cf">
              <p className="flex items-center gap-1.5 text-sm text-forest leading-5 whitespace-nowrap" data-id="element-249" data-mp="37bxtjjdb9jwa">
                <img src={`https://flagcdn.com/${purchase.countryCode}.svg`} alt={`${purchase.country} flag`} className="w-[18px] h-[13px] rounded-[2px] object-cover shrink-0 ring-1 ring-black/5" data-id="element-250" data-mp="32d4bddn2x4ps" />
                <span className="font-bold" data-id="element-251" data-mp="6h8668r0tt6s">{purchase.firstName}</span>
                <span className="text-charcoal/70 truncate" data-id="element-252" data-mp="6evgtaixogjp">
                  from {purchase.city}, {purchase.country}
                </span>
              </p>
              <p className="text-sm text-charcoal/70 leading-5" data-id="element-253" data-mp="fgl72d12x9fw">
                purchased{' '}
                <span className="font-semibold text-forest" data-id="element-254" data-mp="k8wj2e93glot">Hydroponics Grower Ready</span>
              </p>
              <p className="flex items-center gap-1.5 text-xs text-charcoal/60 leading-4 mt-1" data-id="element-255" data-mp="p6o7uzefzros">
                <span className="w-1.5 h-1.5 rounded-full bg-leaf-bright" aria-hidden="true" data-id="element-256" data-mp="u4pqxkbvbr09" />
                {minutesLabel}
              </p>
            </div>

            <button type="button" onClick={() => setIsDismissed(true)} className="absolute top-2 right-2 w-6 h-6 rounded-full text-charcoal/40 hover:text-forest hover:bg-sage-50 flex items-center justify-center transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf" aria-label="Close notification" data-id="element-257" data-mp="137h6nwbs18xp">
              <X size={13} data-id="element-258" data-mp="25lse7tllvc4p" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function isEditorPreview(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}